import { DomainPreferences } from '../../api/types';
import { RegularMetricId } from '../../api/types-graphql';
import {
  DashboardConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2Type,
} from '../../common/components/dashboards/types';
import { Languages } from '../../constants';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  DataTypes,
  EmployeeDataFields,
  FinancialYearSingleValueByYearsTimeSelection,
  SegmentationByNonHierarchicalField,
  TimeSelectionType,
} from '../../types';
import { dateShiftedByMonths, format, monthEnd, now as nowUtils } from '../../utils-date';
import { StoryboardDateUtils } from './utils';

const dateUtils = new StoryboardDateUtils();
const thisMonth = monthEnd(nowUtils());
const now = thisMonth;

export const JTBDashboardChartConfig: (domainPreferences: DomainPreferences) => DashboardConfig = (
  domainPreferences: DomainPreferences
) => {
  const orgLevel1Filter = `EMPLOYEE.ORGANIZATION_LEVEL_1 = '(株)JTB'`;
  const orgLevel2Filter1 = `EMPLOYEE.ORGANIZATION_LEVEL_2 = 'グローバル' OR 
  EMPLOYEE.ORGANIZATION_LEVEL_2 = 'ツーリズム' OR 
  EMPLOYEE.ORGANIZATION_LEVEL_2 = 'エリアソリューション' OR 
  EMPLOYEE.ORGANIZATION_LEVEL_2 = 'ビジネスソリューション' OR 
  EMPLOYEE.ORGANIZATION_LEVEL_2 = '事業基盤機能' OR 
  EMPLOYEE.ORGANIZATION_LEVEL_2 = '休職者など'`;
  const orgLevel2Filter2 = `EMPLOYEE.ORGANIZATION_LEVEL_2 = '出向者'`;

  const orgLevel1AndOrgLevel2Filter1 = `(${orgLevel1Filter}) AND (${orgLevel2Filter1})`;
  const orgLevel1AndOrgLevel2Filter2 = `(${orgLevel1Filter}) AND (${orgLevel2Filter2})`;

  const customField1Filter = `EMPLOYEE.CUSTOM_FIELD_1_LEVEL_1 = 'キャリア・既卒' OR EMPLOYEE.CUSTOM_FIELD_1_LEVEL_1 = '新卒'`;
  const customField4Filter = `EMPLOYEE.CUSTOM_FIELD_4_LEVEL_1 = '(株)JTB'`;

  const customField1AndCustomField4Filter = `(${customField1Filter}) AND (${customField4Filter})`;

  const managersFilter = `EMPLOYEE.CUSTOM_FIELD_3_LEVEL_1 = '管理監督者'`;
  const nonManagersFilter = `EMPLOYEE.CUSTOM_FIELD_3_LEVEL_1 = '管理監督者以外'`;

  const orgLevel1AndManagersFilter = `(${orgLevel1Filter}) AND (${managersFilter})`;
  const orgLevel1AndNonManagersFilter = `(${orgLevel1Filter}) AND (${nonManagersFilter})`;

  const orgLevel2Segmentation: SegmentationByNonHierarchicalField = {
    type: 'non-hierarchical',
    dataField: { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.ORGANIZATION_LEVEL_2 },
    filters: [
      'グローバル',
      'ツーリズム',
      'エリアソリューション',
      'ビジネスソリューション',
      '事業基盤機能',
      '休職者など',
    ],
  };

  const { nowEndOfMonth, fiscalYear, fiscalYearStart } = new StoryboardDateUtils();
  const currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: nowEndOfMonth, end: nowEndOfMonth },
  };

  const fyYtdToCurrentMonthTimeSelection: FinancialYearSingleValueByYearsTimeSelection = {
    type: TimeSelectionType.FinancialYearSingleValueByYears,
    input: {
      start: fiscalYear(domainPreferences),
      end: fiscalYear(domainPreferences),
    },
  };

  const twoMonthsAgoDate = monthEnd(dateShiftedByMonths(now, -2));

  const twoMonthsAgoTimeSelection: CalendarYearSingleValueByMonthsTimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: format(twoMonthsAgoDate), end: format(twoMonthsAgoDate) },
  };

  const fyYtdToTwoMonthsAgoTimeSelection: CalendarYearSingleValueByMonthsTimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: fiscalYearStart(domainPreferences, twoMonthsAgoDate),
      end: format(twoMonthsAgoDate),
    },
  };

  const footer1 = {
    [Languages.EN]: 'Num Registered Employees at JTB Corporation',
    [Languages.JA]: '（株）JTB本籍従業員の数字',
  };
  const footer2 = {
    [Languages.EN]: 'Num Employees working at JTB Corporation',
    [Languages.JA]: '（株）JTBで勤務する従業員の数字',
  };
  return {
    id: 'jtb_custom_db_1',
    title: {
      [Languages.EN]: 'KPI and Related Metrics',
      [Languages.JA]: 'KPIと関連指標',
    },
    enabled: true,
    pages: [
      {
        pageId: '1',
        dashboardId: 'jtb_custom_db_1',
        pageNumber: 1,
        enabled: true,
        title: {
          [Languages.EN]: 'Page 1',
          [Languages.JA]: 'ページ１',
        },
        pageOptions: {
          displayOptions: {
            enabled: false,
          },
          timeSlider: {
            enabled: false,
          },
          filterTray: {
            enabled: false,
          },
          dataView: {
            enabled: false,
          },
          segmentationLevel1: {
            enabled: false,
          },
          segmentationLevel2: {
            enabled: false,
          },
          benchmark: {
            enabled: false,
          },
          forecast: {
            enabled: false,
          },
        },
        gridCols: 20,
        components: [
          {
            chartId: '1',
            title: {
              [Languages.EN]: 'Individuals',
              [Languages.JA]: '従業員数',
            },
            footer: footer1,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 5,
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'individuals',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0001_1IndividualsCount }],
                  timeSelection: currentMonthTimeSelection,
                  filters: orgLevel1AndOrgLevel2Filter1,
                  segmentations: [orgLevel2Segmentation],
                },
              },
            },
          },
          {
            chartId: '2',
            title: {
              [Languages.EN]: 'Individuals',
              [Languages.JA]: '従業員数',
            },
            subtitle: {
              [Languages.EN]: '(Seconded employees)',
              [Languages.JA]: '(出向者)',
            },
            footer: footer1,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 5,
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'individuals-seconded-employees',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0001_1IndividualsCount }],
                  timeSelection: currentMonthTimeSelection,
                  filters: orgLevel1AndOrgLevel2Filter2,
                },
              },
            },
          },
          {
            chartId: '3',
            title: {
              [Languages.EN]: '# of Joiners',
              [Languages.JA]: '入社者',
            },
            subtitle: {
              [Languages.EN]: '(Career + New Graduates)',
              [Languages.JA]: '(キャリア+新卒)',
            },
            footer: footer1,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 5,
            options: {
              kpiType: KpiV2Type.DOUBLE_METRIC,
              sections: {
                leftSection: {
                  key: 'joiners-current-month',
                  title: {
                    [Languages.EN]: '(Most recent month)',
                    [Languages.JA]: '(最新の月間値)',
                  },
                  query: {
                    metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0006_1JoinersJoiners }],
                    timeSelection: currentMonthTimeSelection,
                    filters: customField1AndCustomField4Filter,
                  },
                },
                rightSection: {
                  key: 'joiners-ytd',
                  title: {
                    [Languages.EN]: '(FYTD)',
                    [Languages.JA]: '(期初からの累計)',
                  },
                  query: {
                    metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0006_1JoinersJoiners }],
                    timeSelection: fyYtdToCurrentMonthTimeSelection,
                    filters: customField1AndCustomField4Filter,
                  },
                },
              },
            },
          },
          {
            chartId: '4',
            title: {
              [Languages.EN]: 'Voluntary Attrition',
              [Languages.JA]: '自己都合離職者',
            },
            subtitle: {
              [Languages.EN]: '(FYTD)',
              [Languages.JA]: '(期初からの累計)',
            },
            footer: footer1,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 5,
            align: 'row',
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'voluntary-attrition-ytd',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0237_4VoluntaryAttritionLeavers }],
                  timeSelection: fyYtdToCurrentMonthTimeSelection,
                  filters: customField4Filter,
                },
                benchmarkConfigs: [
                  {
                    benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
                    benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                  },
                ],
              },
            },
          },
          {
            chartId: '5',
            title: {
              [Languages.EN]: 'Female Headcount',
              [Languages.JA]: '女性従業員',
            },
            subtitle: {
              [Languages.EN]: '(Most recent month)',
              [Languages.JA]: '(最新の月間値)',
            },
            footer: footer1,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 4,
            align: 'row',
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'female-headcount',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0020_2FhcPercHc }],
                  timeSelection: currentMonthTimeSelection,
                  filters: customField4Filter,
                },
              },
            },
          },
          {
            chartId: '6',
            title: {
              [Languages.EN]: 'Female Managers (Defined)',
              [Languages.JA]: '女性管理職',
            },
            subtitle: {
              [Languages.EN]: '(Most recent month)',
              [Languages.JA]: '(最新の月間値)',
            },
            footer: footer1,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 4,
            align: 'row',
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'female-managers-current-month',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0016_3FMngDefPercMng }],
                  timeSelection: currentMonthTimeSelection,
                  filters: customField4Filter,
                },
              },
            },
          },
          {
            chartId: '7',
            title: {
              [Languages.EN]: 'Avg Work Hours per Person',
              [Languages.JA]: '総労働時間',
            },
            subtitle: {
              [Languages.EN]: '(2 months ago)',
              [Languages.JA]: '(前々月の値)',
            },
            footer: footer2,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 4,
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'total-work-hours',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh }],
                  timeSelection: twoMonthsAgoTimeSelection,
                  filters: orgLevel1Filter,
                },
                subQueries: [
                  {
                    title: {
                      [Languages.EN]: 'Managers',
                      [Languages.JA]: '管理監督者',
                    },
                    query: {
                      metrics: [
                        { type: 'RegularMetricId', value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh },
                      ],
                      timeSelection: twoMonthsAgoTimeSelection,
                      filters: orgLevel1AndManagersFilter,
                    },
                  },
                  {
                    title: {
                      [Languages.EN]: 'Non Managers',
                      [Languages.JA]: '上記以外',
                    },
                    query: {
                      metrics: [
                        { type: 'RegularMetricId', value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh },
                      ],
                      timeSelection: twoMonthsAgoTimeSelection,
                      filters: orgLevel1AndNonManagersFilter,
                    },
                  },
                ],
              },
            },
          },
          {
            chartId: '8',
            title: {
              [Languages.EN]: 'Avg Overtime per Employee',
              [Languages.JA]: '所定外労働時間',
            },
            subtitle: {
              [Languages.EN]: '(2 months ago)',
              [Languages.JA]: '(前々月の値)',
            },
            footer: footer2,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 4,
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'overtime',
                query: {
                  metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0215_2MonthlyOtSumPerHcWithOt }],
                  timeSelection: twoMonthsAgoTimeSelection,
                  filters: orgLevel1Filter,
                },
              },
            },
          },
          {
            chartId: '9',
            title: {
              [Languages.EN]: 'Statutory Annual Paid Leave',
              [Languages.JA]: '有給取得日数',
            },
            subtitle: {
              [Languages.EN]: '(Cumulative Val till 2 months ago)',
              [Languages.JA]: '(期初から前々月の累計値)',
            },
            footer: footer2,
            chartTypeConfig: { chartType: 'kpi-v2' },
            gridColSpan: 4,
            align: 'row',
            options: {
              kpiType: KpiV2Type.SINGLE_METRIC,
              section: {
                key: 'voluntary-attrition-ytd',
                query: {
                  metrics: [
                    { type: 'RegularMetricId', value: RegularMetricId.Pm0381_2StryAnlPaidLeavenotempCumSumPerHc },
                  ],
                  timeSelection: fyYtdToTwoMonthsAgoTimeSelection,
                  filters: orgLevel1Filter,
                },
              },
            },
          },
        ],
      },
    ],
  };
};
